import { useEffect, useMemo, useRef, useState } from 'react';

import { useBreakPoints } from 'core/Hooks';
import {
  ActionIcon,
  Box,
  BoxProps,
  Combobox,
  Group,
  Loader,
  Modal,
  NoResults,
  ScrollArea,
  Skeleton,
  Text,
  TextInput,
  ThemeIcon,
  TransitionElement,
  cx,
  useCombobox,
} from 'ui';
import { useDebouncedValue, useUncontrolled } from 'ui/Hooks';
import { IconSearch, IconVerified, IconX } from 'ui/Icons';

import classes from './Search.module.css';

type SearchProps<T> = {
  /**
   * A callback function that should return an object with the data and a boolean indicating if the data is loading.
   * This is used to fetch the data based on the search term.
   */
  useSearchFn: (searchTerm?: string) => {
    data: T[];
    isLoading: boolean;
  };

  /**
   * A callback function that should return a React node for each item.
   * This is used to render the option in the dropdown.
   */
  renderOption: (item: T) => React.ReactNode;

  /**
   * A callback function that should return a unique identifier for the item.
   * This is used when mapping over the items to create the options.
   */
  optionKey: (item: T) => string;

  /**
   * A callback function that should return a value for each item.
   * This is the value that gets passed when the option is selected.
   */
  optionValue: (item: T) => string;
  placeholder?: string;

  /**
   * Callback fired when an option is selected via mouse or keyboard
   * @param value The value of the selected option
   * @param combobox The combobox store instance
   * @example
   * ```tsx
   * <Search
   *   onOptionSubmit={(value, combobox) => {
   *     // Navigate to cause page
   *     navigate(`/causes/${value}`);
   *     // Close the dropdown
   *     combobox.closeDropdown();
   *   }}
   * />
   * ```
   */
  onOptionSubmit?: (value: string, combobox: ReturnType<typeof useCombobox>) => void;

  focusOnMount?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  defaultValue?: string;
  showVerifiedBadge?: boolean;
} & BoxProps;

const Search = <T extends object>({
  className,
  useSearchFn,
  renderOption,
  optionKey,
  optionValue,
  placeholder = 'Search...',
  onOptionSubmit,
  focusOnMount,
  value,
  onChange,
  defaultValue,
  showVerifiedBadge = false,
  ...props
}: SearchProps<T>) => {
  const { isSmallScreen } = useBreakPoints();

  const [search, setSearch] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });

  const [debouncedSearch] = useDebouncedValue(search, 500);

  const { data, isLoading } = useSearchFn(debouncedSearch);

  const optionKeyRef = useRef<(item: T) => string>(optionKey);
  const optionValueRef = useRef<(item: T) => string>(optionValue);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    opened: isSmallScreen ? true : undefined,
  });

  const options = useMemo(
    () =>
      data.map((item) => (
        <Combobox.Option value={optionValueRef.current(item)} key={optionKeyRef.current(item)}>
          {renderOption(item)}
        </Combobox.Option>
      )),
    [data, renderOption]
  );

  const rightSection = useMemo(
    () =>
      isLoading ? (
        <Loader size={18} />
      ) : search ? (
        <ActionIcon
          size="sm"
          variant="transparent"
          c="gray"
          aria-label="Clear value"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={() => setSearch('')}
        >
          <IconX brand="tabler" />
        </ActionIcon>
      ) : null,
    [isLoading, search]
  );

  // Used to calculate the height of the overlay based on the bottom of the viewport
  const searchRef = useRef<HTMLDivElement>(null);
  const [searchTop, setSearchTop] = useState(0);

  useEffect(() => {
    if (searchRef.current) {
      const rect = searchRef.current.getBoundingClientRect();
      setSearchTop(rect.top);
    }
  }, []);

  return (
    <Box ref={searchRef} className={cx(classes.Search, className)} {...props}>
      <TransitionElement mounted={combobox.dropdownOpened} duration={200} timingFunction="ease">
        {!isSmallScreen && (
          <Box
            className={classes.Search__overlay}
            style={{
              '--top-position': `${searchTop}px`,
            }}
            mod={{ 'full-screen': isSmallScreen, 'no-search': !search }}
          />
        )}
      </TransitionElement>

      <Combobox
        classNames={classes}
        store={combobox}
        offset={0}
        onOptionSubmit={(val) => {
          setSearch(val);
          onOptionSubmit?.(val, combobox);
          combobox.closeDropdown();
        }}
        transitionProps={{
          duration: 200,
          transition: 'fade',
          timingFunction: 'ease',
          enterDelay: 250,
        }}
        middlewares={{
          shift: false,
        }}
      >
        <Combobox.Target>
          <TextInput
            // the mod prop doesn't work with mantine's `data-autofocus` attribute
            data-autofocus={focusOnMount}
            size="lg"
            classNames={{
              root: classes.Search__inputRoot,
              input: classes.Search__inputInput,
            }}
            placeholder={placeholder}
            leftSection={<IconSearch />}
            rightSection={rightSection}
            value={search}
            onChange={(event) => {
              const newValue = event.currentTarget.value;
              setSearch(newValue);
              combobox.openDropdown();
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <TransitionElement
              mounted={!isLoading && options.length > 0}
              duration={300}
              enterDelay={250}
              transition="fade"
              timingFunction="ease"
            >
              <Group justify="space-between" align="center" px="md">
                <Text fz={14} c="gray" fw={600}>
                  Search Results
                </Text>
                {showVerifiedBadge && (
                  <Group gap="xs">
                    <Text fz={10} fw={600} c="cobalt.5">
                      Active on Givebacks
                    </Text>
                    <ThemeIcon color="cobalt.5" variant="transparent" size={16}>
                      <IconVerified />
                    </ThemeIcon>
                  </Group>
                )}
              </Group>
            </TransitionElement>

            <ScrollArea.Autosize
              mah={{
                base: `calc(100vh - ${searchTop}px - 200px)`,
                sm: `calc(100vh - ${searchTop}px - 144px)`,
              }}
              type="scroll"
            >
              {isLoading && (
                <Combobox.Empty px="2">
                  <Skeleton w="100%" h={80} />
                  <Skeleton w="100%" h={80} mt="2" />
                  <Skeleton w="100%" h={80} mt="2" />
                  <Skeleton w="100%" h={80} mt="2" />
                  <Skeleton w="100%" h={80} mt="2" />
                  <Skeleton w="100%" h={80} mt="2" />
                  <Skeleton w="100%" h={80} mt="2" />
                </Combobox.Empty>
              )}
              <TransitionElement
                mounted={!isLoading && options.length > 0}
                duration={300}
                enterDelay={250}
                timingFunction="ease"
              >
                {options}
              </TransitionElement>

              <TransitionElement
                mounted={!!search && !isLoading && options.length === 0}
                duration={300}
                enterDelay={250}
                timingFunction="ease"
              >
                <Combobox.Empty>
                  <NoResults />
                </Combobox.Empty>
              </TransitionElement>
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
};

/**
 * Wraps the Search component in a modal if on a small screen.
 */
const WrappedSearch = <T extends object>(props: Omit<SearchProps<T>, 'value' | 'onChange'>) => {
  const { isSmallScreen } = useBreakPoints();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const allProps = props;
  const { className, placeholder, ...restProps } = allProps;

  return isSmallScreen ? (
    <Box className={cx(classes.Search, className)} {...restProps}>
      <TextInput
        component="button"
        type="button"
        size="lg"
        classNames={{
          root: classes.Search__inputRoot,
          input: classes.Search__inputInput,
        }}
        leftSection={<IconSearch />}
        onClick={() => setIsModalOpen(true)}
      >
        {search || placeholder}
      </TextInput>
      <Modal
        fullScreen={isSmallScreen}
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        classNames={{
          body: classes.Search__modalBody,
          content: classes.Search__modalContent,
        }}
        transitionProps={{
          duration: 200,
          transition: 'fade',
          timingFunction: 'ease',
          exitDuration: 0,
        }}
      >
        <Search {...allProps} value={search} onChange={setSearch} focusOnMount />
      </Modal>
    </Box>
  ) : (
    <Search {...allProps} value={search} onChange={setSearch} />
  );
};

export { WrappedSearch as Search };
